const CX = 200;
const CY = 200;
const R = 140;
const AMBER = '#e0a63a';
const AMBER_LIGHT = '#f2b94a';

// Ellipse width per meridian; i=4 is the edge-on meridian (rx≈0) so it is skipped.
const MERIDIANS = [0, 1, 2, 3, 5, 6, 7].map((i) => ({
  id: i,
  rx: Math.abs(R * Math.cos((i / 8) * Math.PI)),
}));
const PARALLELS = [1, 2, 3, 4, 5].map((i) => ({
  y: CY + R * Math.cos((i / 6) * Math.PI),
  rx: R * Math.sin((i / 6) * Math.PI),
}));
/** Upper 100° arc of a circle centred on the globe's north pole — the wifi-style signal fan. */
function arcAbove(originY: number, radius: number): string {
  const spread = (50 * Math.PI) / 180;
  const x1 = CX - radius * Math.sin(spread);
  const x2 = CX + radius * Math.sin(spread);
  const y = originY - radius * Math.cos(spread);
  return `M${x1},${y} A${radius},${radius} 0 0 1 ${x2},${y}`;
}

const PARTICLES = [
  { x: CX - 80, y: CY - 60, d: 0.2 },
  { x: CX + 90, y: CY + 40, d: 0.8 },
  { x: CX + 60, y: CY - 70, d: 0.5 },
];

/** Intact wireframe globe with a severed uplink — the world is fine, the link is not. */
export function SignalLostGlobe() {
  return (
    <svg viewBox="0 -40 400 440" className="h-full w-full" aria-hidden focusable="false">
      <defs>
        <radialGradient id="sl-globe-fill" cx="50%" cy="45%" r="50%">
          <stop offset="0%" stopColor="rgba(224,166,58,0.07)" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
        <filter id="sl-glow">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <clipPath id="sl-clip">
          <circle cx={CX} cy={CY} r={R} />
        </clipPath>
      </defs>

      <circle
        cx={CX}
        cy={CY}
        r={R}
        fill="url(#sl-globe-fill)"
        stroke={AMBER}
        strokeWidth="1"
        opacity="0.22"
      />

      <g clipPath="url(#sl-clip)" opacity="0.22" fill="none" stroke={AMBER} strokeWidth="0.6">
        {MERIDIANS.map((m) => (
          <ellipse key={`m${m.id}`} cx={CX} cy={CY} rx={m.rx} ry={R} strokeDasharray="12 8 3 8" />
        ))}
        {PARALLELS.map((p, i) => (
          <ellipse
            key={`p${i}`}
            cx={CX}
            cy={p.y}
            rx={p.rx}
            ry={p.rx * 0.15}
            strokeDasharray="6 12 2 8"
          />
        ))}
      </g>

      {/* Signal arcs rising from the globe: the first two hold, the third breaks up before the satellite. */}
      <g filter="url(#sl-glow)" stroke={AMBER_LIGHT} strokeLinecap="round" fill="none">
        <path d={arcAbove(CY - R, 22)} strokeWidth="2.2" opacity="0.9" />
        <path d={arcAbove(CY - R, 40)} strokeWidth="2" opacity="0.6" />
        <path
          d={arcAbove(CY - R, 58)}
          strokeWidth="1.8"
          opacity="0.35"
          strokeDasharray="5 9"
          className="sl-uplink"
        />
        <circle cx={CX} cy={CY - R - 82} r="3" fill={AMBER_LIGHT} stroke="none" opacity="0.9" />
        <circle
          cx={CX}
          cy={CY - R - 82}
          r="8"
          strokeWidth="1"
          opacity="0.35"
          strokeDasharray="3 4"
        />
      </g>

      <circle
        cx={CX}
        cy={CY}
        r={R}
        fill="none"
        stroke={AMBER_LIGHT}
        strokeWidth="1.5"
        opacity="0.3"
        strokeDasharray="20 5 3 5"
      />
      <circle
        cx={CX}
        cy={CY}
        r={R + 12}
        fill="none"
        stroke={AMBER}
        strokeWidth="1.2"
        strokeDasharray="120 760"
        opacity="0.7"
        className="sl-orbit"
      />

      <g fill={AMBER_LIGHT}>
        {PARTICLES.map((p) => (
          <circle
            key={`${p.x}-${p.y}`}
            cx={p.x}
            cy={p.y}
            r="2"
            opacity="0"
            className="sl-particle"
            style={{ animationDelay: `${p.d}s` }}
          />
        ))}
      </g>
    </svg>
  );
}
